import React, { useEffect, useRef } from 'react';
import { useChat } from '@/hooks';
import { useAssistants } from '@/hooks/useAssistants';
import { ChatInput } from '../components/chat/ChatInput';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { EmptyState } from '../components/chat/EmptyState';
import { LoadingState } from '../components/common/LoadingState';
import { EmptyAssistantsState } from '../components/common/EmptyAssistantState';
import { AssistantSelectionPage } from '../components/assistant/AssistantSelectionPage';
import { AdService } from '@services/ads';
import { gtmService } from '@/services/retargeting/gtm.service';
import { fbPixelService } from '@/services/retargeting/fb.pixel.service';

interface ChatPageProps {
    showBanner: () => void;
}

export const ChatPage: React.FC<ChatPageProps> = ({ showBanner }) => {
    const {
        messages,
        isLoading,
        streamingMessage,
        selectedAssistant,
        sendMessage,
        setSelectedAssistant,
        startNewChat,
        currentTool,
    } = useChat();

    const { assistants, loading: loadingAssistants } = useAssistants();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const adService = AdService.getInstance();

    const assistant = assistants[selectedAssistant] || null;

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingMessage]);

    const handlePromptClick = (prompt: string) => {
        sendMessage(prompt);
    };

    const handleAssistantSelection = (assistantId: string) => {
        const selectedAssistantData = assistants[assistantId];

        if (selectedAssistantData) {
            // Track with Google Ads using GTM service
            gtmService.trackAssistantSelected(
                selectedAssistantData.name,
                selectedAssistantData.category
            );

            // Track with Facebook Pixel service
            fbPixelService.trackAssistantSelection(
                selectedAssistantData.name,
                selectedAssistantData.category
            );
        }

        setSelectedAssistant(assistantId);
        startNewChat();
    };

    // Get milestone ad if applicable
    const milestoneAd = messages.length > 0 && messages.length % 10 === 0
        ? adService.getAd({ assistantType: selectedAssistant, messageCount: messages.length })
        : null;

    // Loading state
    if (loadingAssistants) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <LoadingState message="Loading assistants..." />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // No assistants available
    if (Object.keys(assistants).length === 0) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <EmptyAssistantsState />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Assistant selection page
    if (!assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-4xl mx-auto">
                        <AssistantSelectionPage
                            assistants={assistants}
                            onAssistantSelect={handleAssistantSelection}
                        />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Chat page with selected assistant
    return (
        <>
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        {messages.length === 0 ? (
                            <EmptyState
                                assistant={assistant}
                                onPromptClick={handlePromptClick}
                            />
                        ) : (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                milestoneAd={milestoneAd}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};